// External Libraries
import * as React from "react";
import { inject, observer } from "mobx-react";

// Types
import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import { ThumborStore, AspectRatioEnum } from "stores/thumbor/ThumborStore";

// UITK
import { UitkCardLink, UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { classNames } from "uitk-react-utils";

// Components
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useHotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";
import { SummarizedContent } from "components/flexComponents/Hotels/components/SummarizedHotelCard/SummarizedContent";

// Utils
import { getThumborizedImage } from "src/components/flexComponents/Hotels/utils/getThumborizedImage";

interface SummarizedHotelCardProps {
  hotel: Hotel;
  index: number;
  isMobile: boolean;
  thumbor?: ThumborStore;
  className?: string;
  imageAspectRatio?: UitkFigureAspectRatioType;
}

export const SummarizedHotelCardComponent: React.FC<SummarizedHotelCardProps> = ({
  hotel,
  index,
  isMobile,
  thumbor,
  className,
  imageAspectRatio,
}) => {
  const { flexTemplateNode, context } = useHotelsContext();
  const hotelImageUrl = getThumborizedImage(hotel, thumbor!, context, {
    mobile: AspectRatioEnum.R_1_1,
    desktop: AspectRatioEnum.R_4_3,
  });
  const { hotelName, hotelInfositeUrl } = hotel;
  const moduleName = flexTemplateNode.options.flexComponentName;

  return (
    <UitkCardAloha className={classNames("SummarizedHotelCard", className)}>
      <UitkLayoutGrid columns={6} data-testid="summarized-hotelcard">
        <>
          <UitkLayoutGridItem colSpan={2}>
            <UitkFigure
              ratio={imageAspectRatio || (isMobile ? UitkFigureAspectRatioType.R1_1 : UitkFigureAspectRatioType.R4_3)}
            >
              <LazyLoadedImage src={hotelImageUrl} alt={hotelName} />
            </UitkFigure>
          </UitkLayoutGridItem>
          <UitkLayoutGridItem colSpan={4}>
            <UitkCardAlohaContentSection>
              <SummarizedContent hotel={hotel} isMobile={isMobile} />
              <UitkCardLink>
                <TrackedLink
                  data-testid={`hotelcard-link-${index}`}
                  className="uitk-card-link"
                  moduleName={moduleName}
                  rfrr="card"
                  href={hotelInfositeUrl}
                >
                  {hotelName}
                </TrackedLink>
              </UitkCardLink>
            </UitkCardAlohaContentSection>
          </UitkLayoutGridItem>
        </>
      </UitkLayoutGrid>
    </UitkCardAloha>
  );
};

export const SummarizedHotelCard = inject("thumbor")(observer(SummarizedHotelCardComponent));

SummarizedHotelCard.displayName = "SummarizedHotelCard";
